import { useState } from 'react'
import { useEditor } from '../store/useEditor'
import { PRODUCTS } from '../lib/products'
import { sectionsOf } from '../lib/sections'
import { takeoff, takeoffWording } from '../lib/materials'
import { Section } from './bits'

/**
 * WHAT GOES ON THE TRUCK.
 *
 * Four numbers and, under each of them, the arithmetic that produced it. The
 * explanation is not documentation: it comes back from the same function that
 * counted, so the two cannot drift apart. A contractor who cannot see how a
 * count was reached recounts it by hand, and then the takeoff is worth nothing.
 *
 * The unverified figure sits BESIDE each number rather than being subtracted
 * from it. This list is what somebody orders against, so the honest shape is
 * "77 bulbs, 21 of them off a guess", never one confident 77.
 */
export default function Materials() {
  const doc = useEditor((s) => s.doc)
  const defaults = useEditor((s) => s.defaults)
  const [openKey, setOpenKey] = useState<string | null>(null)

  const sections = sectionsOf(doc)
  /*
   * Spacing is a real physical property of the product, and the run's spacing
   * SCALE is a look control - a person dragging bulbs closer together on the
   * mockup is changing how it reads, not what is on the wire. So the count
   * comes from the product, which is the number the warehouse can act on.
   */
  const product = PRODUCTS[defaults.product] ?? PRODUCTS.C9
  const t = takeoff(sections, product.spacingInches)

  if (sections.length === 0) return null

  return (
    <Section title="What this takes" right={<span className="val">{product.label}</span>}>
      <p className={t.solid ? 'sec-gate ok' : 'sec-gate warn'}>{takeoffWording(t)}</p>

      <div className="mat">
        {t.lines.map((l) => (
          <div key={l.key} className="mat-row">
            <button
              className="mat-head"
              onClick={() => setOpenKey(openKey === l.key ? null : l.key)}
              aria-expanded={openKey === l.key}
            >
              <span className="mat-label">{l.label}</span>
              <span className="mat-qty">
                {l.qty}
                {l.unit ? ` ${l.unit}` : ''}
              </span>
              {l.fromUnverified > 0 && (
                <span className="mat-guess">
                  {Math.round(l.fromUnverified)}
                  {l.unit ? ` ${l.unit}` : ''} off a guess
                </span>
              )}
            </button>
            {openKey === l.key && <p className="mat-how">{l.how}</p>}
          </div>
        ))}
      </div>

      {/*
        NOT A PURCHASE ORDER, AND SAYING SO IS THE POINT.

        There is no catalog behind this, no stock on hand and no pack sizes, so
        what is here is what the job needs and not what to buy. MAT-01 is
        explicit that no counts means stock unknown rather than all stocked,
        and a screen that quietly implied otherwise would be worse than no
        screen.
      */}
      <p className="hint" style={{ margin: '8px 0 0' }}>
        What the job needs. Stock on hand, pack sizes and spare cable are not in
        this yet, so it is not an order.
      </p>
    </Section>
  )
}
